import Backbone from 'backbone';
import {Dq} from "collection/Dq";
import Paramdiv from "collection/ParamdivCollection";
import _ from 'lodash';
import {checkPersurlRemoteCollision} from 'module/BSEntry/checkPersurlRemoteCollision';
import {CF} from "module/CustomField/util/CustomField";
import EntryDescription from 'module/edesc/EntryDescription';
import {validateDateLimits} from "module/edesc/validation/BookingEndApprovalValidation";
import moment from 'moment';
import ErrMsgDict from 'string/ErrorMessageStrings';
import {parseSisDate, parseSisDuration, parseSisTime} from 'util/SisDateTime';
import {CustomFieldTO, SisType} from "generated/JavaTO"
import {validateRemoteCFVali} from 'module/edesc/validation/CFVali';
import {ZspArt} from 'module/edesc/ZspArt';
import {getSizeFromBase64} from 'util/FileReaderUtil';
import {BSApiAction} from 'module/BSEntry/enum/BSApiAction';
import {Perm, TypePerm} from 'module/edesc/TypePerm';
import {showDeleteConfirm, showPersurlCollisionMessage} from 'module/edesc/validation/ValidationConfirmMessages';
import {getCurrentUserPersNr} from 'module/SessionInfo/api/SessionInfoAPI';
import {getCustomFieldsFrom} from 'module/CustomField/util/getCustomFieldsFrom';
import {kbSizeToString} from 'util/kbSizeToString';
import {Booking, EntryDesc, Persurl} from 'generated/JavaTOmodifications'
import {FieldError} from 'module/CustomField/type/FieldError'
import {log} from 'common/log'
import {EntryDescStateType} from 'module/edesc/reducer/EntryDescReducer'
import {PersonStateType} from 'module/Person/reducer/PersonReducer'
import {CFMember, CFModel, CFValue, isCFMember, isCFMemberNull} from 'module/CustomField/type/CFType'
import {SisName} from 'util/SisName'
import axios from 'axios'
import {serverErrorHandler} from 'module/ConfirmDialog/component/ConfirmDialogContainer'
import {CFAttrKey, CFAttrType} from 'module/CustomField/constant/CFAttrConstants'

const edCollection = EntryDescription.collection

export class ValidationError extends Error {
	validationResult: FieldError[]

	constructor(...validationResult: FieldError[]) {
		super()
		this.validationResult = _.uniqWith(validationResult, _.isEqual)
	}
}

interface StaticParams {
	bewEinh: {
		required: boolean
		mustMatch: boolean
	}
	empfEinh: {
		required: boolean
		mustMatch: boolean
	}
	text: {
		requiredBookingTypes: string[]
		requiredHourCategories: string[]
	}
}

let _staticParamsPromise: Promise<StaticParams>;

function getStaticParamsPromise(): Promise<StaticParams> {
	if (!_staticParamsPromise) {
		_staticParamsPromise = Paramdiv.fetchMultiplePromise(
			Paramdiv.ID.bewEinhRequired,
			Paramdiv.ID.bewEinhMustMatch,
			Paramdiv.ID.empfEinhRequired,
			Paramdiv.ID.empfEinhMustMatch,
			Paramdiv.ID.requiredTextBookingTypes1,
			Paramdiv.ID.requiredTextBookingTypes2,
			Paramdiv.ID.requiredTextBookingTypes3,
			Paramdiv.ID.requiredTextHourCategories,
		).then(([
			bewEinhRequired,
			bewEinhMustMatch,
			empfEinhRequired,
			empfEinhMustMatch,
			requiredTextBookingTypes1,
			requiredTextBookingTypes2,
			requiredTextBookingTypes3,
			requiredTextHourCategories,
		]: string[][]) => {
			// paramdiv call returns array, type coercion is on purpose
			// noinspection EqualityComparisonWithCoercionJS
			return {
				bewEinh: {
					required: bewEinhRequired?.[0] !== "N",
					mustMatch: bewEinhMustMatch?.[0] !== "N",
				},
				empfEinh: {
					required: empfEinhRequired?.[0] === "J",
					mustMatch: empfEinhMustMatch?.[0] !== "N",
				},
				text: {
					requiredBookingTypes: _.compact([...requiredTextBookingTypes1, ...requiredTextBookingTypes2, ...requiredTextBookingTypes3]),
					requiredHourCategories: _.compact([...requiredTextHourCategories]),
				},
			}
		})
	}
	return _staticParamsPromise
}

interface MatchValueObj {
	id: number,
	matchValue: any,
	errorMsg?: string
}

type MatchValueCollection = { [id in number]?: MatchValueObj }

/**
 * @param defaultMatchValue {*} fallback if not found in matchValueCollection, default: no validation
 * @param errorMsg {String}
 * @param [matchValueCollection] {*} if they are different for entries with special entryDescription
 * @constructor
 */
export class RuleSetMatcher {
	defaultMatchValue: any
	errorMsg: string
	matchValueCollection: MatchValueCollection
	transformErrorMsgVal?: (errorMsgVal: any) => string

	constructor(defaultMatchValue: any, errorMsg: string, matchValueCollection?: MatchValueCollection) {
		this.defaultMatchValue = defaultMatchValue
		this.errorMsg = errorMsg
		this.matchValueCollection = matchValueCollection || []
	}

	getMatchValue(entryDescriptionId: number) {
		let currVal = this.matchValueCollection[entryDescriptionId]
		if (!currVal) return this.defaultMatchValue;
		else return currVal.matchValue
	}

	getErrorMsg(entryDescriptionId: number) {
		let currVal = this.matchValueCollection[entryDescriptionId]
		return currVal && currVal.errorMsg || this.errorMsg
	}
}

const createPromiseFromDeferredResult = (deferred: JQueryDeferred<FieldError[][]>) => Promise.resolve(deferred).catch((...err) => err);

// type ValidatorMethod = (matchVal: any, val: any, attrName: string | number, options: SingleValidationOptions) => any
export const ValidatorDict = {
	gt: function (matchVal: any, val: any) {
		return val > matchVal;
	},
	lt: function (matchVal: any, val: any) {
		return val < matchVal;
	},
	ge: function (matchVal: any, val: any) {
		return val >= matchVal;
	},
	le: function (matchVal: any, val: any) {
		return val <= matchVal;
	},
	mod: function (matchVal: any, val: any) {
		return val % matchVal === 0;
	},
	maxDecimalPlaces: function (matchVal: any, val: any) {
		if (!val) return true;
		let decPlaces = (val + '').split('.')[1];
		return !decPlaces || decPlaces.length <= matchVal;
	},
	required: function (matchVal: any, val: any, attrName: string | number, options: SingleValidationOptions) {
		if (!matchVal) return true;
		switch (options.type) {
			case SisType.Date:
				return val && val > 19700000;
			case SisType.Time:
			case SisType.Duration:
			case SisType.Duration2:
			case SisType.Int:
			case SisType.Float:
				return parseFloat(val);
			default:
				return val && (typeof val !== 'string' || val.trim());
		}
	},
	requiredForKostMM: function (matchVal: any, val: any, attrName: string | number, options: SingleValidationOptions) {
		if (!options.kost || !matchVal || !matchVal.trim()) return true
		let kostMM = options.kost.get("kostMM")
		if (!kostMM || kostMM[matchVal] !== 'J') return true //disabled check when current kost has not mm in list
		return val && (typeof val !== 'string' || val.trim());
	},
	maxLength: function (matchVal: any, val: any) {
		if (val === ' ') return true;
		let length;
		if (!val || _.isEmpty(val)) {
			length = 0;
		} else if (_.isPlainObject(val)) {
			length = _.keysIn(val).length;
		} else {
			length = val.length;
		}
		return length <= matchVal;
	},
	minLengthTrimmedString: function (matchVal: any, val: any) {
		if (!matchVal) return true;
		if (typeof val === 'boolean') return true;
		if (typeof val === 'number') return (val + '').length;
		if (typeof val !== 'string') return false;
		return val.length >= matchVal;
	},
	/**
	 * @param sisType {SisType}
	 * @param val
	 * @returns {boolean|*}
	 */
	type: function (sisType: SisType, val: any) {
		if (!val) return true;
		switch (sisType) {
			case SisType.Int:
			case SisType.Number: //kont type
				return val == parseInt(val);
			case SisType.Float:
			case SisType.Decimal:
				return val == parseFloat(val);
			case SisType.Date:
				return parseSisDate(val).isValid();
			case SisType.Time:
				return parseSisTime(val).isValid();
			case SisType.Duration:
				return moment.isDuration(parseSisDuration(val));
			case SisType.Duration2:
				//todo industrial duration format
				break;
			case SisType.String:
			case SisType.VChar:
				return typeof val === 'string';
			case SisType.Key:
				return val === " " || /^[0-9A-Z\.\+\-:_#/%]*$/.test(val);
			case SisType.Blob:
				return !val || _.isPlainObject(val);
			default:
				return true;
		}
	},
	maxBlobSize(matchVal: any, val: any) {
		let data = val && val.data;
		if (!data || !(data += '').trim() || matchVal < 1) return true;
		const size = getSizeFromBase64(data);
		return size <= (matchVal * 1024);

	},
	geThanAttributeStart: function (matchVal: any, val: any, attrName: string | number, options: SingleValidationOptions) {
		let start = (options?._model as Persurl).start;
		return val >= start;
	},
	leMaxDurationFromModelBoundaries: function (matchVal: any, val: any, attrName: string | number, options: SingleValidationOptions) {
		//todo do not use model boundaries
		let boundaries = options?.boundaries;
		if (!boundaries) {
			log.error('boundaries missing, but ruleset.max_zsp=J ')
			return false;
		}
		let maxDuration = boundaries.get('maxDuration');
		if (maxDuration == null) return true;
		const ret = val <= maxDuration;
		if (!ret) log.debug('maxDuration:', maxDuration)
		return ret;
	},
	aacfMustMatch: function (matchVal: any, val: any, attrNameOrCfDefId: string | number, options: SingleValidationOptions) {
		if (!matchVal || !options || !options.aacf) return true;
		let aacf = options.aacf[attrNameOrCfDefId];
		if (!aacf) return true;
		return createPromiseFromDeferredResult(aacf.getErrorByRules(val, {mustMatch: true}));
	},
	aacfByRuleset: function (matchVal: any, val: any, attrNameOrCfDefId: string | number, options: SingleValidationOptions) {
		if (!options || !_.isPlainObject(options.aacfRuleset) || !options.aacf) return true;
		let aacf = options.aacf[attrNameOrCfDefId];
		if (!aacf) return true;
		return createPromiseFromDeferredResult(aacf.getErrorByRules(val, options.aacfRuleset));
	},
	/**
	 * Last validation! waits for others to complete to not bother user with 'want to overwrite'-question before a valid model exists
	 * always use as last validation
	 */
	checkPersurlCollision: function (matchVal: any, val: any, attrName: string | number, options: SingleValidationOptions) {
		if (_.trim((options._model as Booking).bookingType) || !_.trim(options._model.hourCategory)) return Promise.resolve(); // no persurl
		const model = options._model as Persurl
		const persistedModel = options._persistedModel as any as Persurl;

		return checkPersurlRemoteCollision(persistedModel, model, options).catch(function (err) {
			log.debug(err)
			return showPersurlCollisionMessage(err.collisions);
		});
	},
} as const

type RulesetMethodMap = {
	type: RuleSetMatcher
	required?: RuleSetMatcher
	ge?: RuleSetMatcher
	gt?: RuleSetMatcher
	le?: RuleSetMatcher
	lt?: RuleSetMatcher
	mod?: RuleSetMatcher
	leMaxDurationFromModelBoundaries?: RuleSetMatcher
	requiredForKostMM?: RuleSetMatcher
	maxDecimalPlaces?: RuleSetMatcher
	aacfMustMatch?: RuleSetMatcher
	maxLength?: RuleSetMatcher
	geThanAttributeStart?: RuleSetMatcher
	checkPersurlCollision?: RuleSetMatcher
}
//init from bde.ruleset
let ruleSet = {
	booking: {
		persNr: {
			required: new RuleSetMatcher(true, ErrMsgDict.missingPersNr),
			ge: new RuleSetMatcher(1, ErrMsgDict.missingPersNr),
			type: new RuleSetMatcher(SisType.Int, ErrMsgDict.missingPersNr),
		},
		bookingDate: {
			required: new RuleSetMatcher(true, ErrMsgDict.missingDate),
			ge: new RuleSetMatcher(19700101, ErrMsgDict.formatDate),
			le: new RuleSetMatcher(21991231, ErrMsgDict.formatDate),
			type: new RuleSetMatcher(SisType.Date, ErrMsgDict.formatDate),
		},
		bookingTime: {
			required: new RuleSetMatcher(false, ErrMsgDict.missingTime),
			gt: new RuleSetMatcher(false, ErrMsgDict.missingTime), // required for not-nullable
			ge: new RuleSetMatcher(0, ErrMsgDict.formatTime),
			le: new RuleSetMatcher(235959, ErrMsgDict.formatTime),
			type: new RuleSetMatcher(SisType.Time, ErrMsgDict.formatTime),
		},
		bookingTimespan: {
			required: new RuleSetMatcher(false, ErrMsgDict.missingDurationEndTime),
			gt: new RuleSetMatcher(false, ErrMsgDict.missingDurationEndTime), // required for not-nullable
			le: new RuleSetMatcher(235959, ErrMsgDict.tooHighDuration),
			mod: new RuleSetMatcher(100, ErrMsgDict.formatDuration),
			type: new RuleSetMatcher(SisType.Duration, ErrMsgDict.formatDuration),
			leMaxDurationFromModelBoundaries: new RuleSetMatcher(false, ErrMsgDict.tooHighDuration),
		},
		einstuf: {
			gt: new RuleSetMatcher(false, ErrMsgDict.missingValue), // required for not-nullable
			ge: new RuleSetMatcher(-999.99, ErrMsgDict.tooLowValue),
			le: new RuleSetMatcher(999.99, ErrMsgDict.tooHighValue),
			maxDecimalPlaces: new RuleSetMatcher(2, ErrMsgDict.tooManyDecimalPlaces),
			type: new RuleSetMatcher(SisType.Float, ErrMsgDict.formatInvalid),
		},
		text: {
			required: new RuleSetMatcher(false, ErrMsgDict.missingComment),
			requiredForKostMM: new RuleSetMatcher(false, ErrMsgDict.missingComment),
			maxLength: new RuleSetMatcher(512, ErrMsgDict.tooLongValue),
			type: new RuleSetMatcher(SisType.String, ErrMsgDict.formatInvalid),
			aacfMustMatch: new RuleSetMatcher(false, ErrMsgDict.notAvailableValue),
		},
	} as const,
	persurl: {
		persNr: {
			type: new RuleSetMatcher(SisType.Int, ErrMsgDict.missingPersNr),
			required: new RuleSetMatcher(true, ErrMsgDict.missingPersNr),
			ge: new RuleSetMatcher(1, ErrMsgDict.missingPersNr),
		},
		start: {
			type: new RuleSetMatcher(SisType.Date, ErrMsgDict.formatDate),
			required: new RuleSetMatcher(true, ErrMsgDict.missingDate),
			ge: new RuleSetMatcher(19700101, ErrMsgDict.formatDate),
			le: new RuleSetMatcher(21991231, ErrMsgDict.formatDate),
		},
		end: {
			type: new RuleSetMatcher(SisType.Date, ErrMsgDict.formatDate),
			required: new RuleSetMatcher(true, ErrMsgDict.missingDate),
			ge: new RuleSetMatcher(19700101, ErrMsgDict.formatDate),
			le: new RuleSetMatcher(21991231, ErrMsgDict.formatDate),
			geThanAttributeStart: new RuleSetMatcher(true, ErrMsgDict.endBeforeStartDate),
		},
		text: {
			required: new RuleSetMatcher(false, ErrMsgDict.missingComment),
			maxLength: new RuleSetMatcher(70, ErrMsgDict.tooLongValue),
			type: new RuleSetMatcher(SisType.String, ErrMsgDict.formatInvalid),
			aacfMustMatch: new RuleSetMatcher(false, ErrMsgDict.notAvailableValue),
		},
	} as const,
} as const
//no attr, always last
const persurl_phase2 = {
	checkPersurlCollision: new RuleSetMatcher(true, ErrMsgDict.collisionBlockedSaving),
} as const

function validateModelDidNotChange(validatedAttributes: CFModel, modelAttributesAfterValidation: CFModel) {
	if (!_.isEqual(validatedAttributes, modelAttributesAfterValidation)) {
		return {message: "Die Daten haben sich seit dem letzten Versuch geändert. Bitte versuchen sie es erneut."}
	}
}

function errorMsgFillIn(errorMsg: string, matchVal: any) {
	errorMsg = errorMsg.replace(/%val%/g, matchVal);
	return errorMsg;
}

type SingleResult = FieldError | undefined | null

function intermediateResult(...array: any[]): Promise<SingleResult[]> {
	return Promise.all(_.compact(_.flattenDeep(array)))
}

function postValidation(result: Promise<SingleResult[]>) {
	result.then(
		(x: SingleResult[]) => log.debug('validation result, errors:', x),
	).catch(
		(x: ValidationError) => log.debug('error in validation:', x?.validationResult || x),
	)
	return result.then(prepareBeforeReturn)
}

function isValidationActive(matchValue: any) {
	return !(matchValue === undefined || matchValue === false || matchValue === null);
}

/**
 * @param entryDescriptionIdgetSizeFromBase64
 * @param attrName
 * @param ruleSetMatcherEntry
 * @param validationRuleName
 * @param options
 * @returns {Promise | {field, message} | undefined}
 */
function validateByEntryDescription(entryDescriptionId: number, attrName: string, ruleSetMatcherEntry: RuleSetMatcher, validationRuleName: keyof typeof ValidatorDict, options: SingleValidationOptions) {
	if (!(ruleSetMatcherEntry instanceof RuleSetMatcher)) return; // validation global off
	let model = options._model
	let currValiMatchValue = ruleSetMatcherEntry.getMatchValue(entryDescriptionId)
	//execute validation
	if (!isValidationActive(currValiMatchValue)) return;
	let currValiResult = ValidatorDict[validationRuleName](currValiMatchValue, model[attrName as keyof typeof model], attrName, options)
	if (!currValiResult) {
		let errorMsg = ruleSetMatcherEntry.getErrorMsg(entryDescriptionId)
		let errorMsgVal = currValiMatchValue
		let {transformErrorMsgVal} = ruleSetMatcherEntry
		if (typeof transformErrorMsgVal === 'function') errorMsgVal = transformErrorMsgVal(errorMsgVal)
		errorMsg = errorMsgFillIn(errorMsg, errorMsgVal)
		return {field: attrName, message: errorMsg}
	} else if (currValiResult.then) {
		return currValiResult // wait for result
	} // else no error
	return null
}

interface CFDefMethodDict {
	cfDefAttr: string
	method: typeof ValidatorDict[keyof typeof ValidatorDict]
	errorMsg: string
	transformErrorMsgVal?: (x: any) => string
}

function getCFDefMethodDict(cfDef: CustomFieldTO): CFDefMethodDict[] {
	switch (cfDef.type) {
		case SisType.Blob:
			return [{
				cfDefAttr: 'type',
				method: ValidatorDict.type,
				errorMsg: ErrMsgDict.formatInvalid,
			}, {
				cfDefAttr: 'length',
				method: ValidatorDict.maxBlobSize,
				errorMsg: ErrMsgDict.maxBlobSizeExceeded,
				transformErrorMsgVal: kbSizeToString,
			}]
		case SisType.GPS:
			return [{cfDefAttr: 'type', method: ValidatorDict.type, errorMsg: ErrMsgDict.formatInvalid}];
		default:
			return [
				{cfDefAttr: 'required', method: ValidatorDict.required, errorMsg: ErrMsgDict.missingValue},
				{cfDefAttr: 'length', method: ValidatorDict.maxLength, errorMsg: ErrMsgDict.tooLongValue},
				{cfDefAttr: 'type', method: ValidatorDict.type, errorMsg: ErrMsgDict.formatInvalid},
				{cfDefAttr: 'aacfRuleset', method: ValidatorDict.aacfByRuleset, errorMsg: ErrMsgDict.notAvailableValue},
			];
	}
}

const validateCustomFieldDefinitions = (cfDefs: CustomFieldTO[], options: VOpt) => {
	const model = options._model
	const singleCFDefResult = _.map(cfDefs, function (cfDef) {
		const field = cfDef.id;
		const validationOpts: SingleValidationOptions = {...options, type: cfDef.type}
		if (_.trim(cfDef.dqKey)) {
			Object.assign(validationOpts, {
				aacfRuleset: {
					required: cfDef.required,
					mustMatch: cfDef.mustMatchDq,
					forceDq: cfDef.forceDq,
				},
				aacf: {
					[field]: new Dq({model, cfDef}),
				},
			});
		}
		const vv = CF.getValueFrom(cfDef, model as CFModel)
		let valAll: (string | number | null | undefined | CFMember | {})[] = Array.isArray(vv) ? vv : [vv]
		const valNullFiltered = valAll.filter(x => {
			if (isCFMember(x as CFMember)) {
				return !isCFMemberNull(x as CFMember)
			} else {
				return !CF.isNull(cfDef, x as CFValue)
			}
		});
		const valiMethods = getCFDefMethodDict(cfDef);
		const isCFNamespace = CF.isCFNamespace(cfDef);
		const isRawCF = CF.isRawCF(cfDef);
		let cfNSErrors = [];
		if (isCFNamespace) {
			const amount = valNullFiltered.length;
			if (cfDef.required && amount < cfDef.minAmount) {
				let errorMsg;
				if (!amount) {
					errorMsg = ErrMsgDict.missingValue;
				} else {
					errorMsg = errorMsgFillIn(ErrMsgDict.amountTooLow, cfDef.minAmount)
				}
				cfNSErrors.push({
					field, message: errorMsg,
				});
			} else if (cfDef.maxAmount > 0 && amount > cfDef.maxAmount) {
				cfNSErrors.push({
					field, message: errorMsgFillIn(ErrMsgDict.amountTooHigh, cfDef.maxAmount),
				});
			}
			cfDef.cfAttrs.forEach(cfAttr => {
				if (cfAttr.type !== CFAttrType.Validation) return;
				if (cfAttr.key === CFAttrKey.ForceRequired && cfAttr.value === 'J') {
					cfNSErrors.push(...valAll.flatMap((cfMember: CFMember) => {
						if (!cfMember.lazy && CF.isNull(cfDef, cfMember.data)) {
							return [{field: cfDef.id, pos: cfMember.pos, message: ErrMsgDict.missingValue}]
						}
						return []
					}))
				}
			})
		}
		let validateValues: typeof valAll
		if (isCFNamespace) {
			validateValues = !valNullFiltered.length ? [{} as CFMember] : valNullFiltered
		} else {
			validateValues = valAll
		}
		// ensure minimum of 1 validation per cfDef (required for cfDef.forceDQ)
		let cfErrors = validateValues.map(x => {
			let pos: number | undefined | null;
			let v: CFValue;
			if (isCFNamespace) {
				pos = (x as CFMember)?.pos
				if (!isRawCF) v = (x as CFMember).data
			} else {
				v = (x as CFValue)
			}
			return _.map(valiMethods, function (dict) {
				let matchVal = cfDef[dict.cfDefAttr as keyof CustomFieldTO]
				let currValiResult = dict.method(matchVal, v, cfDef.id, validationOpts)
				if (!currValiResult) {
					let errorMsgVal = matchVal;
					let {transformErrorMsgVal} = dict;
					if (typeof transformErrorMsgVal === 'function') errorMsgVal = transformErrorMsgVal(errorMsgVal);
					let msg = errorMsgFillIn(dict.errorMsg, errorMsgVal);
					const ret: FieldError = {field, message: msg};
					if (isCFNamespace) ret.pos = pos
					return ret;
				} else if (currValiResult.then) {
					return currValiResult as Promise<FieldError>;
				} // else no error
			});
		});
		const cfValiResult = validateCFVali(cfDefs, model);
		if (options.isReport) {
			const cfPromptKeyErrors = validateReportPromptKey(cfDefs)
			return intermediateResult(...cfNSErrors, ...cfErrors, ...cfValiResult, ...cfPromptKeyErrors)
		}
		return intermediateResult(...cfNSErrors, ...cfErrors, ...cfValiResult)
	});
	return intermediateResult(singleCFDefResult)
};

const validateReportPromptKey = (cfdefs: CustomFieldTO[]) => {
	let result = _.map(cfdefs, cfdef => {
		if (_.isNaN(parseInt(cfdef.dstField))) {
			return {field: cfdef.id, message: 'Zielfeld nicht numerisch: [dstField=\'' + cfdef.dstField + '\']'}
		}
	});
	return result;
}

const validateCFVali = (cfDefs: CustomFieldTO[], model: any) =>
	_.flattenDeep(
		_.map(cfDefs, cfDef =>
			_.map(cfDef.cfValiIds, cfValiId =>
				validateRemoteCFVali(cfDef.id, cfValiId, model),
			),
		),
	);

function parseEntryDescriptions() {
	console && typeof console.log === 'function' && console.log('parseEntryDescriptions:' + edCollection.length);
	//extract rules
	edCollection.each(function (edBB: Backbone.Model) {
		const ed: EntryDesc = edBB.attributes
		let sisName = ed.sisName;
		let ruleSetEntry = ruleSet[sisName];
		if (!ruleSetEntry) return;
		//all required
		if (sisName === SisName.Booking) {
			const rs = ruleSetEntry as typeof ruleSet.booking
			if (ed.buchzeitVonZwingend) {
				rs.bookingTime.gt.matchValueCollection[ed.id] = {id: ed.id, matchValue: 0}
			}
			if (ed.buchzspZwingend) {
				rs.bookingTimespan.gt.matchValueCollection[ed.id] = {id: ed.id, matchValue: 0}
			}
			if (ed.zspArt === ZspArt.Saldo) {
				rs.bookingTimespan.le.matchValueCollection[ed.id] = {id: ed.id, matchValue: 99985959}
			}
			if (ed.checkMaxDuration) {
				rs.bookingTimespan.leMaxDurationFromModelBoundaries.matchValueCollection[ed.id] = {
					id: ed.id,
					matchValue: true,
				}
			}
			if (ed.einstufZwingend) {
				rs.einstuf.gt.matchValueCollection[ed.id] = {
					id: ed.id,
					matchValue: 0,
					errorMsg: ed.einstufText + " auswählen",
				}
			}
			rs.einstuf.maxDecimalPlaces.matchValueCollection[ed.id] = {
				id: ed.id,
				matchValue: ed.einstufNK,
			}
		}
		if (ed.butextZwingend) {
			ruleSetEntry.text.required.matchValueCollection[ed.id] = {
				id: ed.id, matchValue: true, errorMsg: ed.butextText + " auswählen",
			}
		}
		ruleSetEntry.text.maxLength.matchValueCollection[ed.id] = {
			id: ed.id,
			matchValue: ed.butextLaenge,
		}
		ruleSetEntry.text.type.matchValueCollection[ed.id] = {
			id: ed.id,
			matchValue: ed.butextTyp,
		}
		let dq = ed.butextDq;
		if (dq && dq.trim()) {
			ruleSetEntry.text.aacfMustMatch.matchValueCollection[ed.id] = {
				id: ed.id,
				matchValue: true,
			}
		}

		let butextKostmmSwitch = ed.butextKostmm;
		if (butextKostmmSwitch && butextKostmmSwitch.trim()) {
			(ruleSetEntry as typeof ruleSet.booking).text.requiredForKostMM.matchValueCollection[ed.id] = ({
				id: ed.id,
				matchValue: butextKostmmSwitch,
				errorMsg: ed.butextText + " auswählen",
			});
		}
	});
}

EntryDescription.fetchOnceDefer.done(() => {
	edCollection.listenTo(edCollection, 'update reset', parseEntryDescriptions);
	parseEntryDescriptions();
});


type Method = "create" | "post" | "insert" | "confirm" | "put" | "update" | "decline" | "delete"
const getApprovalConflicts = (bookingNr: number, method: Method) => {
	return axios.get<string[]>("../json/workflow/approvalConflicts", {
		params: {method, bookingNr},
	})
}


function validateDeleteServerSideAndAskUser(persistedModel: CFModel, entryDesc: EntryDesc | undefined | null, options: VOpt): Promise<(undefined | null)[]> {
	//todo refresh person after deletion
	return Promise.resolve().then(() => {
		if (!entryDesc?.approvalPze && !entryDesc?.approvalPzeKost) return;
		//special case approval bookings
		// first stage: check on server
		return getApprovalConflicts(persistedModel.bookingNr, "delete").then(({data}) => data)
	}).then(conflicts => {
		if (conflicts && conflicts.length) {
			//todo refresh person after deletion
			return "Folgende Anträge werden ebenfalls zurückgewiesen:\n" + conflicts.join("\n");
		} else {
			return null;
		}
	}).catch((err?: any) => {
		serverErrorHandler(err)
		throw new ValidationError({message: "Es ist ein Fehler beim Validieren der Konflikte aufgetreten."})
	}).then(info => info || !options.doNotAskOnDelete ? showDeleteConfirm(persistedModel, info) : undefined)
		.then(() => [undefined])
}

// deprecated functionality applied to Custom Fields
const getStaticParamOverrideCustomFields = (entryDesc: EntryDesc, selfAdmin: boolean, {bewEinh, empfEinh, text}: StaticParams) => {
	const cfDefs = getCustomFieldsFrom(entryDesc, selfAdmin)
	const {sisName} = entryDesc;
	switch (sisName) {
		case SisName.Persurl:
			return cfDefs.map(cfDef => {
				if (cfDef.dstField === 'text' && !cfDef.required && _.includes(text.requiredHourCategories, entryDesc.hourCategory)) {
					cfDef = _.cloneDeep(cfDef);
					cfDef.required = true;
				}
				return cfDef;
			});
		case SisName.Booking:
			return cfDefs.map(cfDef => {
				if (cfDef.dstField === 'text' && !cfDef.required && _.includes(text.requiredBookingTypes, entryDesc.bookingType)) {
					cfDef = _.cloneDeep(cfDef);
					cfDef.required = true;
				} else if (cfDef.dstField === 'bewEinh') {
					if (!cfDef.required && bewEinh.required) {
						cfDef = _.cloneDeep(cfDef);
						cfDef.required = true;
					}
					if (!cfDef.mustMatchDq && bewEinh.mustMatch) {
						cfDef = _.cloneDeep(cfDef);
						cfDef.mustMatchDq = true;
					}
				} else if (cfDef.dstField === 'empfEinh') {
					if (!cfDef.required && empfEinh.required) {
						cfDef = _.cloneDeep(cfDef);
						cfDef.required = true;
					}
					if (!cfDef.mustMatchDq && empfEinh.mustMatch) {
						cfDef = _.cloneDeep(cfDef);
						cfDef.mustMatchDq = true;
					}
				}
				return cfDef;
			});
	}
};

/////////////////////////////////////////////////////////////////////////////////
// ************** Redux validation ***************

export function nextPhaseIfOK(nextPhase: (() => SingleResult[] | Promise<SingleResult[]>) | null, ...errors: SingleResult[][]) {
	const prevErrors = _.compact(_.flattenDeep(errors))
	if (_.isEmpty(prevErrors)) {
		if (nextPhase) return Promise.resolve().then(nextPhase)
		else return [] as FieldError[]
	} else {
		throw new ValidationError(...prevErrors)
	}
}

function prepareBeforeReturn(errors: FieldError[]) {
	return nextPhaseIfOK(null, errors)
}

const validateBSEntryDelete = (persistedModel: CFModel, model: Partial<CFModel>, entryDescById: EntryDescStateType['EntryDesc']['byId'], personsById: PersonStateType['Person']['byId'], options: VOpt) => {
	let valiResult = bsEntryBaseValidation(persistedModel, model, entryDescById, personsById, options);
	valiResult = valiResult.then(
		errorsBefore => nextPhaseIfOK(
			() => validateDeleteServerSideAndAskUser(persistedModel, entryDescById[persistedModel._edId || NaN], options),
			errorsBefore,
		),
	)
	return postValidation(valiResult)
}

const getPermBy = (isNew: boolean, apiAction: BSApiAction) => ({
	[BSApiAction.GET]: Perm.rd,
	[BSApiAction.SAVE]: isNew ? Perm.crt : Perm.upd,
	[BSApiAction.DELETE]: Perm.del,
	[BSApiAction.GEN]: Perm.gen,
	[BSApiAction.ABL]: Perm.abl,
} [apiAction])

const bsEntryBaseValidation = (persistedModel: undefined | null | CFModel, model: Partial<CFModel>, entryDescById: EntryDescStateType['EntryDesc']['byId'], personsById: PersonStateType['Person']['byId'], options: VOpt) => {
	const person = personsById[model.persNr || NaN]
	// validate persnr, entryDescription, permissions
	let errors = [];
	// persNr
	if (!person) {
		errors.push({field: 'persNr', message: 'Person auswählen'});
	} else {
		// person exists
		if (!ValidatorDict.type(SisType.Int, model.persNr)) {
			errors.push({field: 'persNr', message: 'Unbekannte Person'})
		}
		if (!ValidatorDict.maxLength(11, model.persNr)) {
			errors.push({field: 'persNr', message: 'Personalnummer zu lange'})
		}
	}
	// entryDescription
	const eDesc = entryDescById[model._edId || NaN]
	errors.push(validateEntryDescription(persistedModel, model, eDesc, options))
	return intermediateResult(...errors);
};

function validateEntryDescription(persistedModel: undefined | null | CFModel, model: CFModel, eDesc: EntryDesc | undefined | null, options: VOpt) {
	if (!eDesc) {
		return {field: "entryDescription", message: "Bitte Typ des Eintrags auswählen."}
	} else {
		// entryDescription exists -> optional client permission check (to prevent server round trips)
		const {apiAction, userPersNr} = options
		const isNew = _.isEmpty(persistedModel);
		if (apiAction && !TypePerm.check(eDesc, getPermBy(isNew, apiAction), userPersNr === model.persNr)) {
			return {field: "entryDescription", message: "Fehlende Berechtigung für diese Aktion."}
		}
	}
}

/////////////////////////////////////////////////////////////////////////////////

interface BBModel<T> extends Backbone.Model {
	getSisName: () => SisName.Booking | SisName.Persurl
	persistedAttributes: T | undefined | null
}

/**
 * //todo new validations should cancel old validation on el level in case of deferred validation
 * @param bbModel {BookingModel | PersurlModel}
 * @param {{}} [options] can be used to insert external data into all validations
 * @param {boolean} [options.skipStaticValidation] use when application lacks of bde.ruleset fields (but has cfdef) implementation
 * @param {jQuery} [options.$errorDst] form where errors should get rendered as tooltips and marked with css class edValidationError. If this is set, no other error handling must be done, except styling .edValidationError and calling .done on validation result.
 * @param {{}} [options.rulesetExtension] in format {attrname: {validationRule: new RuleSetMatcher(true,'errormsg')}} to add application specific validation logic
 * @param {WorkTimeModelTO} options.boundaries for ruleset.checkMaxDuration validation
 * @returns {$.Deferred} in format: {validationFieldName: String|$.Deferred} String if invalid, defer object when validation has to wait for async data
 */
export function validateEntryBBModel(bbModel: BBModel<CFModel>, options: ValidationOptions) {
	const model = _.cloneDeep(bbModel.attributes);
	let rsModel = _.merge({}, ruleSet[bbModel?.getSisName() as SisName.Booking | SisName.Persurl], options.rulesetExtension);
	if (!bbModel || !_.isPlainObject(rsModel)) {
		console.error("invalid call to validateModel");
		return postValidation(intermediateResult({message: 'Keine Validierungsdefinition gefunden'}));
	}
	const opt: VOpt = {
		...options,
		_model: model,
		_persistedModel: bbModel.persistedAttributes,
	}
	let entryDescription = EntryDescription.collection.getFor(bbModel);
	if (!entryDescription) {
		return postValidation(intermediateResult(
			{field: 'hourCategory', message: ErrMsgDict.missingStuart},
			{field: 'bookingType', message: ErrMsgDict.missingBuart},
		));
	}

	const allValiResults = []

	//cfDef validation, uses numeric cfDef.id as fieldName
	const permResults = validateEntryDescription(opt._persistedModel, model, entryDescription, opt)
	allValiResults.push(permResults);

	//cfDef validation, uses numeric cfDef.id as fieldName
	const selfAdmin = model.persNr === opt.userPersNr
	const cfDefs = entryDescription.getCustomFieldDefinitions(selfAdmin);
	const cfResults = validateCustomFieldDefinitions(cfDefs, opt);
	allValiResults.push(cfResults);

	//static validation (bde.ruleset)
	if (!opt.skipStaticValidation) {
		const staticValidationResult = _.flatten(_.map(rsModel, function (ruleObject: RulesetMethodMap, attrName) {
			if (_.includes(_.map(cfDefs, 'dstField'), attrName)) {
				// CustomFieldDefinitions overrule other definitions before.
				// When customFieldDefinitions uses an existing dstField, that is also contained in static validation,
				// static validations are not used.
				// CustomFieldDefinitions should represent every needed validation.
				return;
			}
			if (!ruleObject.type) console.error("missing required validation rule:", attrName, ruleObject);
			return _.map(ruleObject, function (ruleSetMatcherEntry: RuleSetMatcher, validationRuleName: keyof RulesetMethodMap) {
				return validateByEntryDescription(entryDescription.id, attrName, ruleSetMatcherEntry, validationRuleName, {...opt, type: ruleObject.type.getMatchValue(entryDescription.id)});
			});
		}));
		allValiResults.push(staticValidationResult);
	}

	let lastPhase = intermediateResult(allValiResults);

	if (bbModel.getSisName() === SisName.Persurl) {
		lastPhase = lastPhase.then(
			errorsBefore => nextPhaseIfOK(
				() =>
					validateByEntryDescription(entryDescription.id, '_phase2', persurl_phase2.checkPersurlCollision, 'checkPersurlCollision', opt),
				errorsBefore,
			),
		);
	}
//todo only on save validateModelDidNotChange!
	lastPhase = lastPhase.then(
		errorsBefore => nextPhaseIfOK(
			() => [validateModelDidNotChange(model, bbModel.attributes)],
			errorsBefore,
		),
	);
	return postValidation(lastPhase);
}


export function validateCfDefModel(attr: CFModel, cfDefs: CustomFieldTO[], options?: ValidationOptions) {
	const model = _.cloneDeep(attr)
	const opt: VOpt = {...options, _model: model}
	const lastPhase = validateCustomFieldDefinitions(cfDefs, opt).then(errorsBefore =>
		nextPhaseIfOK(
			() => [validateModelDidNotChange(model, attr)],
			errorsBefore,
		),
	)
	return postValidation(lastPhase)
}

interface ValidationOptions {
	apiAction?: BSApiAction | null
	userPersNr?: number | null
	checkRemoteCollision?: boolean
	doNotAskOnDelete?: boolean
	skipStaticValidation?: boolean
	rulesetExtension?: typeof ruleSet
	boundaries?: Backbone.Model
	kost?: Backbone.Model
	isReport?: boolean
}

interface VOpt extends ValidationOptions {
	_model: Partial<CFModel>
	_persistedModel?: CFModel | null
}

interface SingleValidationOptions extends VOpt {
	type?: SisType
	aacf?: typeof Dq
	aacfRuleset?: {
		required: boolean
		mustMatch: boolean
		forceDq: boolean
	}
}


/**
 *
 * @param persistedModel plain object with attributes (use transformBSToEntryDescId())
 * @param model plain object with attributes (use transformBSToEntryDescId())
 * @param entryDescById
 * @param personsById
 * @param options
 * @param {BSApiAction.*} options.apiAction -> delete validation for ABL / DEL
 * @param {number} options.userPersNr -> used for detecting selfAdmin
 * @param {boolean} [options.doNotAskOnDelete] > skip confirm on deletion for
 * @returns {*}
 */
export function validateBSEntry(persistedModel: undefined | null | CFModel, model: Partial<CFModel>, entryDescById: EntryDescStateType['EntryDesc']['byId'], personsById: PersonStateType['Person']['byId'], options: ValidationOptions) {
	const opt: VOpt = {
		...options,
		// for old validation methods
		_model: model,
		_persistedModel: persistedModel,
	}

	if (typeof opt.userPersNr !== 'number') return postValidation(Promise.reject([{message: 'Personalnummer des Benutzers nicht definiert'}]))

	if (opt.apiAction === BSApiAction.DELETE || opt.apiAction === BSApiAction.ABL) {
		if (!persistedModel) return postValidation(Promise.reject([{message: 'persisted model was missing'}]))
		return validateBSEntryDelete(persistedModel, model, entryDescById, personsById, opt)
	}
	if (!model) return postValidation(Promise.reject([{message: 'model was missing'}]))
	const eDesc = entryDescById[model._edId || NaN] as EntryDesc

	let lastPhase = bsEntryBaseValidation(persistedModel, model, entryDescById, personsById, opt)
	lastPhase = lastPhase.then(
		errorsBefore => nextPhaseIfOK(
			() => getStaticParamsPromise().catch((e) => {
				log.error("Error fetching paramdiv")
				throw e
			}).then((staticParams) => {
				const selfAdmin = model?.persNr === getCurrentUserPersNr()
				const customFields = getStaticParamOverrideCustomFields(eDesc, selfAdmin, staticParams)
				return validateCustomFieldDefinitions(customFields, opt)
			}).catch((...args) => {
				log.error('***** ERROR Validate', ...args)
				return [{message: 'Fehler beim Validieren der Felder'}]
			}),
			errorsBefore,
		),
	)
	lastPhase = lastPhase.then(
		errorsBefore => nextPhaseIfOK(
			() => model.sisName === SisName.Persurl && validateByEntryDescription(eDesc.id, "end", ruleSet.persurl.end.geThanAttributeStart, "geThanAttributeStart", {...opt, type: SisType.Date}),
			errorsBefore,
		),
	)
	lastPhase = lastPhase.then(
		errorsBefore => nextPhaseIfOK(
			() => validateDateLimits(persistedModel, model, entryDescById, personsById),
			errorsBefore,
		),
	)
	lastPhase = lastPhase.then(
		errorsBefore => nextPhaseIfOK(
			() => model.sisName === SisName.Persurl && validateByEntryDescription(eDesc.id, "_phase2", persurl_phase2.checkPersurlCollision, "checkPersurlCollision", opt),
			errorsBefore,
		),
	)
	return postValidation(lastPhase)
}


//this module implicitly fetches entryDescriptionModule.collection
export default {
	validateEntryBBModel,
	RuleSetMatcher: RuleSetMatcher,
};